import { FormattedMessage } from 'react-intl';

import { isTransactionIncludedInAccountBalance } from '@arrived/common';
import { OfferingId, OfferingTransaction } from '@arrived/models';
import { useTradesQuery } from '@arrived/queries';

import { DataStateHandler } from '../../DataStateHandler';
import { OfferingDataList } from './OfferingDataList';
import { OfferingTransactionsListItem } from './OfferingTransactionsListItem';

export interface OfferingTransactionsListProps {
  offeringId: OfferingId;
  className?: string;
}

export const OfferingTransactionList = ({ className, offeringId }: OfferingTransactionsListProps) => {
  const tradesState = useTradesQuery();

  // Trades state could be idle in the case that the user doesn't have an account, in that case we don't want to show
  // an infinite loading image which would be provided by the DataStateHandler if we didn't check for isIdle here.
  if (tradesState.isLoading && tradesState.fetchStatus === 'idle') {
    return null;
  }

  return (
    <DataStateHandler queries={[tradesState]}>
      {(tradesState) => (
        <OfferingTransactionsListContent
          className={className}
          trades={tradesState.data.filter(
            (offeringTransaction) =>
              offeringTransaction.offeringId === offeringId &&
              isTransactionIncludedInAccountBalance(offeringTransaction)
          )}
        />
      )}
    </DataStateHandler>
  );
};

interface OfferingTransactionsListContentProps {
  className?: string;
  trades: OfferingTransaction[];
}

const OfferingTransactionsListContent = ({ className, trades }: OfferingTransactionsListContentProps) => {
  if (trades.length === 0) {
    return null;
  }

  return (
    <OfferingDataList
      className={className}
      title={<FormattedMessage id="transactions" defaultMessage="Transactions" />}
    >
      {trades.map((offeringTransaction) => (
        <OfferingTransactionsListItem key={offeringTransaction.id} offeringTransaction={offeringTransaction} />
      ))}
    </OfferingDataList>
  );
};
